import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CircleIcon from "@mui/icons-material/Circle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { noTranslateProps } from "../../common/NoTranslate";

// --- HOOKS ---
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { useMetaTags } from "../../../hooks/useMetaTags";

// --- CUSTOM SCROLL SPY HOOK ---
const useScrollSpy = (ids: string[], offset = 150) => {
  const [activeId, setActiveId] = useState<string>(ids[0]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + offset;
      const currentSection = ids.reduce((acc, id) => {
        const element = document.getElementById(id);
        if (element && element.offsetTop <= scrollPosition) {
          return id;
        }
        return acc;
      }, ids[0]);
      setActiveId(currentSection);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [ids, offset]);
  return activeId;
};

// --- STYLED HELPERS ---
const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="h5"
    gutterBottom
    sx={{
      fontWeight: 700,
      color: "text.primary",
      mb: 2,
      fontSize: { xs: "1.2rem", md: "1.4rem" },
    }}
  >
    {children}
  </Typography>
);

const SectionText = ({
  children,
  sx,
}: {
  children: React.ReactNode;
  sx?: any;
}) => (
  <Typography
    variant="body1"
    paragraph
    sx={{
      fontSize: { xs: "0.875rem", sm: "1rem" },
      lineHeight: 1.7,
      color: "text.secondary",
      ...sx,
    }}
  >
    {children}
  </Typography>
);

const ShippingPolicy: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isMobile } = useBreakpoint();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const SECTIONS = useMemo(
    () => [
      {
        id: "shipping-disclaimer",
        title: t("shippingPolicyPage.sections.shippingDisclaimer"),
      },
      { id: "fixed-fees", title: t("shippingPolicyPage.sections.fixedFees") },
      {
        id: "dimensional-weight",
        title: t("shippingPolicyPage.sections.dimensionalWeight"),
      },
      {
        id: "important-notes",
        title: t("shippingPolicyPage.sections.importantNotes"),
      },
      {
        id: "transparent-pricing",
        title: t("shippingPolicyPage.sections.transparentPricing"),
      },
      {
        id: "prohibited-items",
        title: t("shippingPolicyPage.sections.prohibitedItems"),
      },
      {
        id: "restricted-items",
        title: t("shippingPolicyPage.sections.restrictedItems"),
      },
      {
        id: "customer-responsibility",
        title: t("shippingPolicyPage.sections.customerResponsibility"),
      },
      { id: "faq", title: t("shippingPolicyPage.sections.faq") },
    ],
    [t]
  );

  const FIXED_FEES_DATA = useMemo(
    () => [
      { item: t("shippingPolicyPage.fixedFees.macbook"), price: "$80" },
      { item: t("shippingPolicyPage.fixedFees.laptop"), price: "$80" },
      { item: t("shippingPolicyPage.fixedFees.iphone"), price: "$80" },
      { item: t("shippingPolicyPage.fixedFees.appleWatch"), price: "$25" },
      { item: t("shippingPolicyPage.fixedFees.ipad"), price: "$50" },
      { item: t("shippingPolicyPage.fixedFees.airpods"), price: "$15" },
      { item: t("shippingPolicyPage.fixedFees.metaGlasses"), price: "$15" },
      {
        item: t("shippingPolicyPage.fixedFees.playstation"),
        price: t("shippingPolicyPage.fixedFees.byWeight"),
      },
      { item: t("shippingPolicyPage.fixedFees.airpodsMax"), price: "$20" },
      { item: t("shippingPolicyPage.fixedFees.ebookReaders"), price: "$10" },
    ],
    [t]
  );

  const PROHIBITED_ITEMS_DATA = useMemo(
    () => [
      {
        title: t("shippingPolicyPage.prohibited.weapons.title"),
        items: [
          t("shippingPolicyPage.prohibited.weapons.item1"),
          t("shippingPolicyPage.prohibited.weapons.item2"),
          t("shippingPolicyPage.prohibited.weapons.item3"),
        ],
      },
      {
        title: t("shippingPolicyPage.prohibited.medicines.title"),
        items: [
          t("shippingPolicyPage.prohibited.medicines.item1"),
          t("shippingPolicyPage.prohibited.medicines.item2"),
          t("shippingPolicyPage.prohibited.medicines.item3"),
          t("shippingPolicyPage.prohibited.medicines.item4"),
        ],
      },
      {
        title: t("shippingPolicyPage.prohibited.dangerous.title"),
        items: [
          t("shippingPolicyPage.prohibited.dangerous.item1"),
          t("shippingPolicyPage.prohibited.dangerous.item2"),
          t("shippingPolicyPage.prohibited.dangerous.item3"),
        ],
      },
      {
        title: t("shippingPolicyPage.prohibited.electronics.title"),
        items: [
          t("shippingPolicyPage.prohibited.electronics.item1"),
          t("shippingPolicyPage.prohibited.electronics.item2"),
          t("shippingPolicyPage.prohibited.electronics.item3"),
        ],
      },
      {
        title: t("shippingPolicyPage.prohibited.valuables.title"),
        items: [
          t("shippingPolicyPage.prohibited.valuables.item1"),
          t("shippingPolicyPage.prohibited.valuables.item2"),
        ],
      },
      {
        title: t("shippingPolicyPage.prohibited.information.title"),
        items: [
          t("shippingPolicyPage.prohibited.information.item1"),
          t("shippingPolicyPage.prohibited.information.item2"),
          t("shippingPolicyPage.prohibited.information.item3"),
        ],
      },
      {
        title: t("shippingPolicyPage.prohibited.food.title"),
        items: [
          t("shippingPolicyPage.prohibited.food.item1"),
          t("shippingPolicyPage.prohibited.food.item2"),
          t("shippingPolicyPage.prohibited.food.item3"),
        ],
      },
      {
        title: t("shippingPolicyPage.prohibited.alcohol.title"),
        items: [
          t("shippingPolicyPage.prohibited.alcohol.item1"),
          t("shippingPolicyPage.prohibited.alcohol.item2"),
        ],
      },
    ],
    [t]
  );

  const FAQ_DATA = useMemo(
    () => [
      {
        question: t("shippingPolicyPage.faq.q1.question"),
        answer: [
          t("shippingPolicyPage.faq.q1.answer1"),
          t("shippingPolicyPage.faq.q1.answer2"),
          t("shippingPolicyPage.faq.q1.answer3"),
        ],
      },
      {
        question: t("shippingPolicyPage.faq.q2.question"),
        answer: [t("shippingPolicyPage.faq.q2.answer1")],
      },
    ],
    [t]
  );

  const sectionIds = useMemo(() => SECTIONS.map((s) => s.id), [SECTIONS]);
  const activeSection = useScrollSpy(sectionIds);

  useMetaTags({
    title: "Shipping Policy Package - US2TJK",
    description:
      "Shipping weight and cost disclaimer for US2TJK services operated by Chai Services LLC",
    keywords: "shipping policy, US2TJK, Chai Services LLC, shipping costs",
  });

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 140;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <Box
      sx={{ minHeight: "50vh", py: { xs: 3, md: 5 } }}
      {...noTranslateProps}
    >
      <Container maxWidth="xl" disableGutters sx={{ px: 2 }}>
        {/* --- MOBILE NAV --- */}
        {isMobile && (
          <Paper
            variant="outlined"
            sx={{ mb: 4, p: 2, bgcolor: "grey.50" }}
            className="notranslate"
            translate="no"
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              sx={{ cursor: "pointer" }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                {t("shippingPolicyPage.onThisPage")}
              </Typography>
              <IconButton size="small">
                <ExpandMoreIcon
                  sx={{
                    transform: mobileMenuOpen
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </IconButton>
            </Box>

            <Box sx={{ display: mobileMenuOpen ? "block" : "none", mt: 2 }}>
              {SECTIONS.map((section, idx) => (
                <Link
                  key={section.id}
                  component="button"
                  onClick={() => scrollToSection(section.id)}
                  sx={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    py: 1,
                    textDecoration: "none",
                    color:
                      activeSection === section.id
                        ? theme.palette.warning.main
                        : theme.palette.text.secondary,
                    fontWeight: activeSection === section.id ? 700 : 400,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    fontSize: "0.9rem",
                  }}
                >
                  {idx + 1}. {section.title}
                </Link>
              ))}
            </Box>
          </Paper>
        )}

        <Box sx={{ display: "flex", gap: 6 }}>
          {/* --- DESKTOP SIDEBAR --- */}
          {isMdUp && (
            <Box
              sx={{ width: 260, flexShrink: 0 }}
              className="notranslate"
              translate="no"
            >
              <Box sx={{ position: "sticky", top: 140 }}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    color: "text.secondary",
                    mb: 2,
                  }}
                >
                  {t("shippingPolicyPage.onThisPage")}
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                  {SECTIONS.map((section) => {
                    const isActive = activeSection === section.id;
                    return (
                      <Box component="li" key={section.id} sx={{ mb: 0 }}>
                        <Link
                          onClick={() => scrollToSection(section.id)}
                          sx={{
                            display: "block",
                            cursor: "pointer",
                            textDecoration: "none",
                            fontSize: "0.875rem",
                            py: 0.75,
                            pl: 2,
                            borderLeft: "3px solid",
                            borderColor: isActive
                              ? "warning.main"
                              : "transparent",
                            color: isActive ? "text.primary" : "text.secondary",
                            fontWeight: isActive ? 600 : 400,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              color: "warning.main",
                              borderColor: "warning.light",
                              bgcolor: "grey.50",
                            },
                          }}
                        >
                          {section.title}
                        </Link>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}

          {/* --- MAIN CONTENT AREA --- */}
          <Box
            sx={{ flex: 1, maxWidth: { md: "800px" } }}
            className="notranslate"
            translate="no"
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "text.primary",
                mb: 4,
                letterSpacing: "-0.5px",
              }}
            >
              {t("shippingPolicyPage.title")}
            </Typography>

            {/* 1. SHIPPING DISCLAIMER */}
            <Box
              id="shipping-disclaimer"
              sx={{ scrollMarginTop: "140px", mb: 6 }}
            >
              <SectionTitle>
                {t("shippingPolicyPage.sections.shippingDisclaimer")}
              </SectionTitle>
              <SectionText>
                {t("shippingPolicyPage.content.disclaimer1")}
              </SectionText>
              <SectionText>
                {t("shippingPolicyPage.content.disclaimer2")}{" "}
                <span translate="no">$14</span>{" "}
                {t("shippingPolicyPage.content.disclaimer3")}
              </SectionText>
            </Box>

            {/* 2. FIXED FEES */}
            <Box id="fixed-fees" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle>
                {t("shippingPolicyPage.content.fixedFeesTitle")}
              </SectionTitle>
              <Paper
                variant="outlined"
                sx={{ overflow: "hidden", borderRadius: 2 }}
              >
                {FIXED_FEES_DATA.map((item, index) => (
                  <Box
                    key={item.item}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      p: 2,
                      bgcolor: index % 2 === 0 ? "white" : "grey.50",
                      borderBottom:
                        index !== FIXED_FEES_DATA.length - 1
                          ? `1px solid ${theme.palette.divider}`
                          : "none",
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      • {item.item}
                    </Typography>
                    <Typography
                      translate="no"
                      variant="body2"
                      fontWeight={700}
                      color="text.primary"
                    >
                      {item.price}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Box>

            {/* 3. DIMENSIONAL WEIGHT */}
            <Box
              id="dimensional-weight"
              sx={{ scrollMarginTop: "140px", mb: 6 }}
            >
              <SectionTitle>
                {t("shippingPolicyPage.content.dimensionalWeightTitle")}
              </SectionTitle>
              <SectionText>
                {t("shippingPolicyPage.content.dimensionalWeight1")}
              </SectionText>

              <Box
                sx={{
                  p: 3,
                  my: 3,
                  bgcolor: "grey.50",
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: "grey.400",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    fontFamily: "monospace",
                    color: "text.primary",
                  }}
                >
                  {t("shippingPolicyPage.content.dimensionalFormula")}
                </Typography>
              </Box>

              <SectionText>
                {t("shippingPolicyPage.content.dimensionalWeight2")}
              </SectionText>
              <SectionText>
                {t("shippingPolicyPage.content.dimensionalWeight3")}
              </SectionText>
            </Box>

            {/* 4. IMPORTANT NOTES */}
            <Box id="important-notes" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle>
                {t("shippingPolicyPage.sections.importantNotes")}
              </SectionTitle>
              <SectionText>
                {t("shippingPolicyPage.content.importantNotes1")}
              </SectionText>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  lineHeight: 1.7,
                  color: "text.secondary",
                }}
                dangerouslySetInnerHTML={{
                  __html: t("shippingPolicyPage.content.importantNotes2"),
                }}
              />
            </Box>

            {/* 5. TRANSPARENT PRICING */}
            <Box
              id="transparent-pricing"
              sx={{ scrollMarginTop: "140px", mb: 6 }}
            >
              <SectionTitle>
                {t("shippingPolicyPage.content.transparentPricingTitle")}
              </SectionTitle>
              <SectionText>
                {t("shippingPolicyPage.content.transparentPricing1")}
              </SectionText>
              <SectionText>
                {t("shippingPolicyPage.content.transparentPricing2")}
              </SectionText>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "flex-start",
                  mt: 3,
                  p: 2,
                  bgcolor: "#FFF8DC",
                  borderLeft: "4px solid #FFD700",
                  borderRadius: 1,
                }}
              >
                <WarningAmberIcon sx={{ color: "#DAA520", mt: 0.5 }} />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  {t("shippingPolicyPage.content.transparentPricingWarning")}
                </Typography>
              </Box>
            </Box>

            {/* 6. PROHIBITED ITEMS */}
            <Box id="prohibited-items" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle>
                {t("shippingPolicyPage.content.prohibitedTitle")}
              </SectionTitle>

              <SectionText sx={{ fontWeight: 600, color: "text.primary" }}>
                {t("shippingPolicyPage.content.prohibitedSubtitle")}
              </SectionText>
              <SectionText>
                {t("shippingPolicyPage.content.prohibited1")}
              </SectionText>
              <SectionText>
                {t("shippingPolicyPage.content.prohibited2")}
              </SectionText>

              <Box sx={{ mb: 4, p: 2, bgcolor: "error.50", borderRadius: 2 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  color="error.main"
                  gutterBottom
                >
                  {t("shippingPolicyPage.content.violationWarning")}
                </Typography>
                <List dense>
                  {[
                    t("shippingPolicyPage.content.violation1"),
                    t("shippingPolicyPage.content.violation2"),
                    t("shippingPolicyPage.content.violation3"),
                    t("shippingPolicyPage.content.violation4"),
                  ].map((item) => (
                    <ListItem key={item}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CircleIcon sx={{ width: 6, color: "error.main" }} />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                {t("shippingPolicyPage.content.strictlyProhibited")}
              </Typography>

              {PROHIBITED_ITEMS_DATA.map((section) => (
                <Box key={section.title} sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <BlockIcon color="error" fontSize="small" /> {section.title}
                  </Typography>
                  <Paper variant="outlined" sx={{ bgcolor: "grey.50" }}>
                    <List dense>
                      {section.items.map((item) => (
                        <ListItem
                          key={item}
                          sx={{
                            borderBottom: "1px dashed",
                            borderColor: "divider",
                            "&:last-child": { borderBottom: "none" },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CircleIcon
                              sx={{ fontSize: 6, color: "text.secondary" }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{
                              fontSize: "0.9rem",
                              color: "text.secondary",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Box>
              ))}
            </Box>

            {/* 7. RESTRICTED ITEMS */}
            <Box id="restricted-items" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle>
                {t("shippingPolicyPage.content.restrictedTitle")}
              </SectionTitle>
              <SectionText>
                {t("shippingPolicyPage.content.restricted1")}
              </SectionText>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                  mt: 2,
                }}
              >
                {[
                  t("shippingPolicyPage.restricted.item1"),
                  t("shippingPolicyPage.restricted.item2"),
                  t("shippingPolicyPage.restricted.item3"),
                  t("shippingPolicyPage.restricted.item4"),
                ].map((item) => (
                  <Paper
                    key={item}
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                    }}
                  >
                    <CheckCircleIcon
                      color="warning"
                      fontSize="small"
                      sx={{ mt: 0.3 }}
                    />
                    <Typography variant="body2" fontWeight={500}>
                      {item}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>

            {/* 8. CUSTOMER RESPONSIBILITY */}
            <Box
              id="customer-responsibility"
              sx={{ scrollMarginTop: "140px", mb: 6 }}
            >
              <SectionTitle>
                {t("shippingPolicyPage.content.responsibilityTitle")}
              </SectionTitle>
              <SectionText>
                {t("shippingPolicyPage.content.responsibility1")}
              </SectionText>
              <SectionText>
                {t("shippingPolicyPage.content.responsibility2")}
              </SectionText>

              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ mt: 3, mb: 1 }}
              >
                {t("shippingPolicyPage.content.ifProhibited")}
              </Typography>
              <List dense sx={{ mb: 2 }}>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <CircleIcon sx={{ fontSize: 6 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("shippingPolicyPage.content.disposed")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <CircleIcon sx={{ fontSize: 6 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("shippingPolicyPage.content.returned")}
                  />
                </ListItem>
              </List>

              <SectionText sx={{ fontWeight: 600 }}>
                {t("shippingPolicyPage.content.finalDecision")}
              </SectionText>

              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ mt: 3, mb: 1 }}
              >
                {t("shippingPolicyPage.content.needHelp")}
              </Typography>
              <SectionText>
                {t("shippingPolicyPage.content.helpText")}
              </SectionText>
            </Box>

            {/* 9. FAQ (DYNAMICALLY RENDERED) */}
            <Box id="faq" sx={{ scrollMarginTop: "140px", mb: 4 }}>
              <SectionTitle>
                {t("shippingPolicyPage.sections.faq")}
              </SectionTitle>
              {FAQ_DATA.map((item, index) => (
                <Accordion
                  key={index}
                  sx={{
                    boxShadow: 0,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    mb: 2, // Added spacing between items if multiple exist
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      bgcolor: "grey.50",
                      "&:hover": { bgcolor: "grey.100" },
                    }}
                  >
                    <Typography fontWeight={600}>{item.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    {item.answer.map((paragraph, i) => (
                      <SectionText
                        key={i}
                        sx={{ mb: i === item.answer.length - 1 ? 0 : 2 }}
                      >
                        {paragraph}
                      </SectionText>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ShippingPolicy;
